// src/resources/customerRfm/api.ts
//
// Chamadas custom da tela "Segmentos de valor" (RFM) — spec
// docs/superpowers/specs/2026-09-29-segmentos-rfm-design.md §6.2, plano
// docs/superpowers/plans/2026-09-29-segmentos-rfm.md Task A3.
//
// `/customer-rfm/*` usa query PLANA (chainId, segment, mainPlaceIds, ...),
// igual a `/customer-lifecycle/*` (ver customerLifecycle/api.ts), diferente do
// resto do admin (que embrulha tudo em `filter={...}` via
// src/store/providers/getAll.js). O backend faz `JSON.parse` de cada valor de
// query com fallback pra string crua (src/server.ts do backend), então tanto
// `chainId="<uuid>"` (com aspas) quanto `r=5` (sem aspas) chegam certos — por
// isso as chamadas vão direto por `httpClientReactAdmin` + URLSearchParams em
// vez do dataProvider genérico.
import { DEFAULT_API_ADMIN } from '../../constants';
import { httpClientReactAdmin } from '../../lib';
import { RfmCutoffs, RfmGridCell, RfmFlow, RfmLeft, RfmSegment, RfmSegmentAggregate } from './segments';

export interface RfmFilters {
  chainId: string;
  segment?: RfmSegment;
  previousSegment?: RfmSegment | 'new';
  r?: number;
  f?: number;
  highValue?: boolean;
  mainPlaceIds?: string[];
  search?: string;
}

export interface RfmSummary {
  computedAt: string | null;
  computing: boolean;
  total: number;
  baseTotal: number;
  segments: (RfmSegmentAggregate & { avgFillins?: number; medianDaysSinceLast?: number })[];
  grid: RfmGridCell[];
  flows: RfmFlow[];
  left: RfmLeft[];
  newCustomers: number;
  cutoffs: RfmCutoffs;
  history: { month: string; segment: RfmSegment; customers: number; totalValue: number; highValue: number }[];
  filteredByPlace: boolean;
}

export interface RfmCustomer {
  id: string;
  name: string;
  cpf: string | null;
  cellphone: string | null;
  segment: RfmSegment;
  previousSegment: RfmSegment | null;
  rScore: number;
  fScore: number;
  highValue: boolean;
  lastFillinAt: string;
  daysSinceLast: number;
  fillinCount: number;
  totalValue: number;
  mainPlaceId: string | null;
  mainPlaceName: string | null;
  tags: { id: string; name: string; color?: string | null }[];
}

export interface FetchCustomersPage {
  skip: number;
  take: number;
  orderBy: 'totalValue' | 'lastFillinAt' | 'fillinCount' | 'name';
  orderDir: 'ASC' | 'DESC';
}

export interface TagRfmResult {
  tagId: string;
  tagName: string;
  total: number;
  taggedNow: number;
  pending: number;
}

// `search`/`segment`/`mainPlaceIds` vão em JSON (aspas/colchetes preservados pelo
// parser do backend); `r`/`f`/`highValue` são valores simples, sem necessidade de aspas.
const buildParams = (f: Partial<RfmFilters>): URLSearchParams => {
  const params = new URLSearchParams();
  if (f.chainId) params.set('chainId', JSON.stringify(f.chainId));
  if (f.segment) params.set('segment', JSON.stringify(f.segment));
  if (f.previousSegment) params.set('previousSegment', JSON.stringify(f.previousSegment));
  if (f.r) params.set('r', String(f.r));
  if (f.f) params.set('f', String(f.f));
  if (f.highValue) params.set('highValue', 'true');
  if (f.mainPlaceIds?.length) params.set('mainPlaceIds', JSON.stringify(f.mainPlaceIds));
  if (f.search) params.set('search', JSON.stringify(f.search));
  return params;
};

export const fetchRfmSummary = async (chainId: string, mainPlaceIds?: string[]): Promise<RfmSummary> => {
  const params = buildParams({ chainId, mainPlaceIds });
  const url = `${DEFAULT_API_ADMIN}/customer-rfm/summary?${params.toString()}`;
  const { json } = await httpClientReactAdmin(url, { method: 'GET' });
  return (json?.data?.[0] ?? json) as RfmSummary;
};

export const fetchRfmCustomers = async (
  f: RfmFilters,
  page: FetchCustomersPage,
): Promise<{ data: RfmCustomer[]; total: number }> => {
  const params = buildParams(f);
  params.set('skip', String(page.skip));
  params.set('take', String(page.take));
  params.set('orderBy', JSON.stringify(page.orderBy));
  params.set('orderDir', JSON.stringify(page.orderDir));
  const url = `${DEFAULT_API_ADMIN}/customer-rfm/customers?${params.toString()}`;
  const { json } = await httpClientReactAdmin(url, { method: 'GET' });
  return { data: (json?.data ?? []) as RfmCustomer[], total: Number(json?.total ?? 0) };
};

export const fetchRfmCustomerIds = async (
  f: RfmFilters,
): Promise<{ ids: string[]; total: number; truncated: boolean }> => {
  const params = buildParams(f);
  const url = `${DEFAULT_API_ADMIN}/customer-rfm/customer-ids?${params.toString()}`;
  const { json } = await httpClientReactAdmin(url, { method: 'GET' });
  return {
    ids: (json?.data ?? []) as string[],
    total: Number(json?.total ?? 0),
    truncated: !!json?.truncated,
  };
};

export const tagRfmCustomers = async (input: {
  chainId: string;
  tagId?: string;
  tagName?: string;
  color?: string;
  filter: Omit<RfmFilters, 'chainId'>;
}): Promise<TagRfmResult> => {
  const url = `${DEFAULT_API_ADMIN}/customer-rfm/tag`;
  const { json } = await httpClientReactAdmin(url, { method: 'POST', body: JSON.stringify(input) });
  // POST /tag devolve um objeto único (createResponse sem array) — `data` não é lista aqui.
  return (json?.data ?? json) as TagRfmResult;
};

// POST /dashboard-v2/refresh — mesmo contrato do botão "Atualizar" do Painel 360
// (store/providers/create.ts manda `chainId` cru na query só pra este recurso).
export const refreshRollup = async (chainId: string): Promise<void> => {
  const url = `${DEFAULT_API_ADMIN}/dashboard-v2/refresh?chainId=${encodeURIComponent(chainId)}`;
  await httpClientReactAdmin(url, { method: 'POST', body: '{}' });
};
